import { StylesConfig } from "react-select";

import type { MenuAlign, MenuLayout, SelectOption } from "../types";

type UseSelectStylesArgs = {
  menuFitContent: boolean;
  menuIsOpen: boolean;
  menuLayout: MenuLayout | null;
  menuAlign?: MenuAlign;
  externalStyles?: StylesConfig<SelectOption>;
};

export function useSelectStyles({
  menuFitContent,
  menuIsOpen,
  menuLayout,
  menuAlign = "left",
  externalStyles,
}: UseSelectStylesArgs): StylesConfig<SelectOption> {
  const defaultStyles: StylesConfig<SelectOption> = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
      ...(menuLayout
        ? menuAlign === "right"
          ? {
              width: "max-content",
              minWidth: "unset",
              left: menuLayout.boundaryRight ?? menuLayout.right,
              right: "auto",
              top: menuLayout.top,
              transform: "translateX(-100%)",
            }
          : {
              width: "max-content",
              minWidth: menuLayout.width,
              left: menuLayout.left,
              top: menuLayout.top,
            }
        : {}),
    }),
    indicatorsContainer: (base) => ({
      ...base,
      padding: 0,
      paddingRight: 12,
    }),
    clearIndicator: (base) => ({
      ...base,
      display: menuIsOpen ? "flex" : "none",
      padding: 0,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: 0,
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      width: "100%",
      gridTemplateColumns: "1fr",
      "&::after": {
        content: "none",
      },
    }),
    menu: (base) => ({
      ...base,
      ...(menuFitContent
        ? {
            width: "max-content",
            minWidth: "100%",
            marginTop: 0,
          }
        : {}),
    }),
  };

  if (!externalStyles) {
    return defaultStyles;
  }

  return {
    ...(externalStyles as StylesConfig<SelectOption>),
    menuPortal: (base, state) => {
      const merged = defaultStyles.menuPortal!(base, state);
      return externalStyles.menuPortal?.(merged, state) ?? merged;
    },
    indicatorsContainer: (base, state) => {
      const merged = defaultStyles.indicatorsContainer!(base, state);
      return externalStyles.indicatorsContainer?.(merged, state) ?? merged;
    },
    clearIndicator: (base, state) => {
      const merged = defaultStyles.clearIndicator!(base, state);
      return externalStyles.clearIndicator?.(merged, state) ?? merged;
    },
    dropdownIndicator: (base, state) => {
      const merged = defaultStyles.dropdownIndicator!(base, state);
      return externalStyles.dropdownIndicator?.(merged, state) ?? merged;
    },
    input: (base, state) => {
      const merged = defaultStyles.input!(base, state);
      return externalStyles.input?.(merged, state) ?? merged;
    },
    menu: (base, state) => {
      const merged = defaultStyles.menu!(base, state);
      return externalStyles.menu?.(merged, state) ?? merged;
    },
  };
}
